import { $isListItemNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  INDENT_CONTENT_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

interface IndentLimitPluginProps {
  maxIndentLevel?: number;
}

/**
 * Plugin that limits the maximum indentation level for all indentable elements
 * (paragraphs, headings, lists, etc.) to prevent content from going outside 
 * the visible area
 */
export function IndentLimitPlugin({ maxIndentLevel = 5 }: IndentLimitPluginProps) {
  const [editor] = useLexicalComposerContext();


  useEffect(() => {
    const getIndentLevel = (node: any): number => {
      // For list items, count nested list levels
      if ($isListItemNode(node)) {
        let level = 0;
        let currentNode = node;

        while (currentNode) {
          if ($isListItemNode(currentNode)) {
            const parent = currentNode.getParent();
            if (parent && parent.getType() === 'list') {
              level++;
            }
          }
          const parentNode = currentNode.getParent();
          if (!parentNode) break;
          currentNode = parentNode;
        }

        return level;
      }

      // For other elements, use the node's indent property
      if ($isElementNode(node) && typeof node.getIndent === 'function') {
        return node.getIndent();
      }

      return 0;
    };

    const removeListener = mergeRegister(
      editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }

          const anchorNode = selection.anchor.getNode();
          let targetNode = anchorNode;

          // Find the element node that would be indented
          while (targetNode && !$isElementNode(targetNode)) {
            const parentNode = targetNode.getParent();
            if (!parentNode) break;
            targetNode = parentNode;
          }

          if (targetNode && $isElementNode(targetNode)) {
            // Check if this node type can be indented
            if (typeof targetNode.canIndent === 'function' && !targetNode.canIndent()) {
              return false; // Let the default behavior handle it
            }

            const currentLevel = getIndentLevel(targetNode);

            // Prevent indentation if already at max level
            if (currentLevel >= maxIndentLevel) {
              return true; // Block the command
            }
          }

          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),

      // We don't need to intercept OUTDENT_CONTENT_COMMAND since
      // outdenting reduces the level and is always safe
    );

    return removeListener;
  }, [editor, maxIndentLevel]);

  return null;
}